import React from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormGroup,
    FormLabel,
    MenuItem,
    Select,
    Slide,
    Chip,
    Autocomplete,
    TextField
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { SelectChangeEvent, Checkbox, ListItemText } from '@mui/material';
import { amountFormat } from '../../../Component/helper';
import { limitOptions } from '../../../Component/config';
import LimitService from '../../../Services/LimitService';
import AlertDialogPopop from '../../../Component/AlertDialogPopop/AlertDialogPopop';
import InputTextField from '../../../Component/Reusables/InputTextField';
import GroupButton from '../../../Component/Reusables/GroupButton';
import './GeneralSettingDialogPopop.css';
import { GeneralSettingDialogPopopProps, GeneralSettingDialogPopopState, TableData } from './interfaces';
import CustomDialog from './CustomDialog';
import { formatLimit } from '../../../Component/config';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { ShowAlert } from '../../../Component/ShowAlert';
import CheckBox from '../../../Component/Reusables/CheckBox';
import { SyncContext, SyncContextType } from '../../../Contexts/BatchSyncContext';
import { sanitizeNumberInput } from '../../../Component/Reusables/numberUtils';

// Interface for dropdown button label props
interface Option {
    label: string;
    value: string;
}

// Transition component for main dialog animations
const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

//  Main general setting dialog component
class GeneralSettingDialogPopop extends React.Component<GeneralSettingDialogPopopProps, GeneralSettingDialogPopopState> {
    static contextType = SyncContext;
    context!: SyncContextType;
    constructor(props_: GeneralSettingDialogPopopProps) {
        super(props_);
        // Initialize state
        this.state = {
            senderCurrency: 'USD',
            senderCurrencySymbol: '$',
            senderCurrencyLimit: '0.00',
            receiverCurrency: '',
            receiverCurrencySymbol: '',
            receiverCurrencyLimit: "0.00",
            selectedPeriod: 'daily',
            thresholdLimit: '',
            standardHourlyLimit: '0.00',
            standardDailyLimit: '0.00',
            standardWeeklyLimit: '0.00',
            standardMonthlyLimit: '0.00',
            standardYearlyLimit: '0.00',
            premiumHourlyLimit: '0.00',
            premiumDailyLimit: '0.00',
            premiumWeeklyLimit: '0.00',
            premiumMonthlyLimit: '0.00',
            premiumYearlyLimit: '0.00',
            businessHourlyLimit: '0.00',
            businessDailyLimit: '0.00',
            businessWeeklyLimit: '0.00',
            businessMonthlyLimit: '0.00',
            businessYearlyLimit: '0.00',
            regionSpecificLimit: '0.00',
            countryList: [],
            regions: [],
            selectedCountry: 'US',
            selectedRegion: [],
            senderCurrencyList: [],
            receiverCurrencyList: [],
            openViewDetails: false,
            isSaveConfirmed: false,
            isHourlyLimitEnabled: false,
            isDailyLimitEnabled: false,
            isWeeklyLimitEnabled: false,
            isMonthlyLimitEnabled: false,
            isYearlyLimitEnabled: false,
            initialStandardHourlyLimit: '0.00',
            initialStandardDailyLimit: '0.00',
            initialStandardWeeklyLimit: '0.00',
            initialStandardMonthlyLimit: '0.00',
            initialStandardYearlyLimit: '0.00',
            initialPremiumHourlyLimit: '0.00',
            initialPremiumDailyLimit: '0.00',
            initialPremiumWeeklyLimit: '0.00',
            initialPremiumMonthlyLimit: '0.00',
            initialPremiumYearlyLimit: '0.00',
            initialBusinessHourlyLimit: '0.00',
            initialBusinessDailyLimit: '0.00',
            initialBusinessWeeklyLimit: '0.00',
            initialBusinessMonthlyLimit: '0.00',
            initialBusinessYearlyLimit: '0.00',
            senderCountryId: 'US',
            receiverCountryId: "",
            receiverPaymentId: ""
        }
    }

    componentDidMount(): void {
        this.getGeneralSetting();
    }

    // Fetch general settings to populate the dialog values
    private async getGeneralSetting() {
        return new Promise<void>((resolve, reject) => {
            LimitService.getGlobalLimitsByCurrencyv2(this.props.accessToken, this.state.senderCurrency)
                .then(tr => {
                    if (tr.ok) {
                        tr.json().then(resp => {
                            let n_ = resp.weeklyLimit ? resp.weeklyLimit : '0';
                            let updatedAmount = amountFormat(parseFloat(n_.replace(/,/g, '')), 'en-US', 'USD').replace("$", "");
                            this.setState({
                                thresholdLimit: resp.thresholdLimit || '0.00',
                                // Sender Currency
                                senderCurrencyList: Array.isArray(resp.senderCurrency) ? resp.senderCurrency : [],
                                receiverCurrencyList: Array.isArray(resp.receiverCurrency) ? resp.receiverCurrency : [],
                                countryList: resp.countryList || [],
                                //State Lists with limits
                                regions: resp.stateList || [],
                                // Standard Limits
                                standardHourlyLimit: resp.limitsByCategories?.standard?.hourlyLimit || '0.00',
                                standardDailyLimit: resp.limitsByCategories?.standard?.dailyLimit || '0.00',
                                standardWeeklyLimit: resp.limitsByCategories?.standard?.weeklyLimit || '0.00',
                                standardMonthlyLimit: resp.limitsByCategories?.standard?.monthlyLimit || '0.00',
                                standardYearlyLimit: resp.limitsByCategories?.standard?.yearlyLimit || '0.00',
                                // Premium Limits
                                premiumHourlyLimit: resp.limitsByCategories?.premium?.hourlyLimit || '0.00',
                                premiumDailyLimit: resp.limitsByCategories?.premium?.dailyLimit || '0.00',
                                premiumWeeklyLimit: resp.limitsByCategories?.premium?.weeklyLimit || '0.00',
                                premiumMonthlyLimit: resp.limitsByCategories?.premium?.monthlyLimit || '0.00',
                                premiumYearlyLimit: resp.limitsByCategories?.premium?.yearlyLimit || '0.00',
                                // Business Limits
                                businessHourlyLimit: resp.limitsByCategories?.business?.hourlyLimit || '0.00',
                                businessDailyLimit: resp.limitsByCategories?.business?.dailyLimit || '0.00',
                                businessWeeklyLimit: resp.limitsByCategories?.business?.weeklyLimit || '0.00',
                                businessMonthlyLimit: resp.limitsByCategories?.business?.monthlyLimit || '0.00',
                                businessYearlyLimit: resp.limitsByCategories?.business?.yearlyLimit || '0.00',
                                // Enable/disable limits/ APi response needs to be revisit to make it one common on/off
                                isHourlyLimitEnabled: resp.limitsByCategories?.standard?.isHourlyLimitEnabled ? true : false,
                                isDailyLimitEnabled: resp.limitsByCategories?.standard?.isDailyLimitEnabled ? true : false,
                                isWeeklyLimitEnabled: resp.limitsByCategories?.standard?.isWeeklyLimitEnabled ? true : false,
                                isMonthlyLimitEnabled: resp.limitsByCategories?.standard?.isMonthlyLimitEnabled ? true : false,
                                isYearlyLimitEnabled: resp.limitsByCategories?.standard?.isYearlyLimitEnabled ? true : false,
                                // Initial value for to retain original value on on/off
                                initialStandardHourlyLimit: resp.limitsByCategories?.standard?.hourlyLimit || '0.00',
                                initialStandardDailyLimit: resp.limitsByCategories?.standard?.dailyLimit || '0.00',
                                initialStandardWeeklyLimit: resp.limitsByCategories?.standard?.weeklyLimit || '0.00',
                                initialStandardMonthlyLimit: resp.limitsByCategories?.standard?.monthlyLimit || '0.00',
                                initialStandardYearlyLimit: resp.limitsByCategories?.standard?.yearlyLimit || '0.00',
                                // Premium Limits
                                initialPremiumHourlyLimit: resp.limitsByCategories?.premium?.hourlyLimit || '0.00',
                                initialPremiumDailyLimit: resp.limitsByCategories?.premium?.dailyLimit || '0.00',
                                initialPremiumWeeklyLimit: resp.limitsByCategories?.premium?.weeklyLimit || '0.00',
                                initialPremiumMonthlyLimit: resp.limitsByCategories?.premium?.monthlyLimit || '0.00',
                                initialPremiumYearlyLimit: resp.limitsByCategories?.premium?.yearlyLimit || '0.00',
                                // Business Limits
                                initialBusinessHourlyLimit: resp.limitsByCategories?.business?.hourlyLimit || '0.00',
                                initialBusinessDailyLimit: resp.limitsByCategories?.business?.dailyLimit || '0.00',
                                initialBusinessWeeklyLimit: resp.limitsByCategories?.business?.weeklyLimit || '0.00',
                                initialBusinessMonthlyLimit: resp.limitsByCategories?.business?.monthlyLimit || '0.00',
                                initialBusinessYearlyLimit: resp.limitsByCategories?.business?.yearlyLimit || '0.00'
                            }, () => {
                                this.updateCurrencyLimitByCurrencyCode();
                                resolve();
                            });
                        });
                    } else {
                        reject("Failed to fetch general settings");
                    }
                })
                .catch(err => reject(err));
        });
    }

    // Build and pass view details data to the custom dialog
    getViewDetailsData = (): TableData => {
        const {
            senderCurrency,
            senderCurrencySymbol,
            standardHourlyLimit,
            standardDailyLimit,
            standardWeeklyLimit,
            standardMonthlyLimit,
            standardYearlyLimit,
            premiumHourlyLimit,
            premiumDailyLimit,
            premiumWeeklyLimit,
            premiumMonthlyLimit,
            premiumYearlyLimit,
            businessHourlyLimit,
            businessDailyLimit,
            businessWeeklyLimit,
            businessMonthlyLimit,
            businessYearlyLimit
        } = this.state;
        const formatLimits = (limit: string | number) => formatLimit(limit, "en-US", senderCurrency, senderCurrencySymbol);
        return {
            rowHeaders: ['Daily', 'Weekly', 'Monthly', 'Yearly'],
            standard: [standardDailyLimit, standardWeeklyLimit, standardMonthlyLimit, standardYearlyLimit].map(formatLimits),
            premium: [premiumDailyLimit, premiumWeeklyLimit, premiumMonthlyLimit, premiumYearlyLimit].map(formatLimits),
            business: [businessDailyLimit, businessWeeklyLimit, businessMonthlyLimit, businessYearlyLimit].map(formatLimits)
        };
    };

    // Maps regions to options for the multi-select dropdown
    mapRegionsToOptions = (): Option[] => {
        return this.state.regions.map((region) => ({
            label: `${region.stateName} (${formatLimit(region.stateLimit || 0.00, "en-US", "USD", this.state.senderCurrencySymbol)})`,
            value: region.stateCode,
        }));
    };

    public render() {
        const options: Option[] = [{ label: "All", value: "All" }, ...this.mapRegionsToOptions()];
        const selectedValues = this.state.selectedRegion.includes("All")
            ? options.filter((option) => option.value === "All")
            : options.filter((option) => this.state.selectedRegion.includes(option.value)); // Initial selected values for the multi-select region dropdown
        const { profileSettings } = this.context.state.permissions;
        return (
            <>
                {/* // Main dialog component */}
                <Dialog
                    open={this.props.open}
                    TransitionComponent={Transition}
                    keepMounted
                    onClose={() => {
                        this.handleClose('cancel')
                    }}
                    fullWidth={true}
                    aria-describedby="alert-dialog-slide-description"
                    maxWidth="xl" // Options: 'xs', 'sm', 'md', 'lg', 'xl'
                    className='setting-popup'
                >
                    <DialogTitle>{"General Settings"}</DialogTitle>
                    <DialogContent>
                        <FormGroup>
                            <Box style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                <fieldset className="fieldset-border">
                                    <FormLabel>Common settings</FormLabel>
                                    <Box style={{ display: 'flex', gap: '20px' }}>
                                        <Box style={{ flex: 1 }}>
                                            <FormGroup>
                                                <FormLabel className='input-field-label'>Current: Sender max per transaction</FormLabel>
                                                <Select
                                                    color='secondary'
                                                    variant="outlined"
                                                    value={this.state.senderCountryId}
                                                    onChange={this.handleSenderCurrencyChange}
                                                    fullWidth
                                                >
                                                    {this.state.senderCurrencyList.map((currencyOption) => (
                                                        <MenuItem key={currencyOption.currencySymbol} value={currencyOption.countryId}>
                                                            {`${currencyOption.currencyCode} - ${formatLimit(currencyOption.limitAmount, "en-US", currencyOption.currencyCode, currencyOption.currencySymbol)}`}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormGroup>
                                        </Box>
                                        <Box style={{ flex: 1 }}>
                                            <FormGroup>
                                                <InputTextField
                                                    label="New: Sender max per transaction"
                                                    value={this.state.senderCurrencyLimit}
                                                    startAdornment={this.state.senderCurrencySymbol}
                                                    inputType="number"
                                                    onChange={(value) => this.handleLimitChange('senderCurrencyLimit', value)}
                                                    onBlur={() => this.handleLimitBlur('senderCurrencyLimit')}
                                                    onFocus={() => this.handleLimitFocus('senderCurrencyLimit')}
                                                />
                                            </FormGroup>
                                        </Box>
                                        <Box style={{ flex: 1 }}>
                                            <FormGroup>
                                                <FormLabel className='input-field-label'>Current: Receiver max per transaction</FormLabel>
                                                <Select
                                                    color='secondary'
                                                    variant="outlined"
                                                    value={this.state.receiverPaymentId}
                                                    onChange={this.handleReceiverCurrencyChange}
                                                    fullWidth
                                                >
                                                    {this.state.receiverCurrencyList.map((currencyOption) => (
                                                        <MenuItem key={currencyOption.paymentMethodId} value={currencyOption.paymentMethodId}>
                                                            {`${currencyOption.currencyCode} - 
                                                              ${formatLimit(currencyOption.limitAmount, "en-US", currencyOption.currencyCode, currencyOption.currencySymbol)} -
                                                              ${currencyOption.paymentMethod}
                                                            `}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormGroup>
                                        </Box>
                                        <Box style={{ flex: 1 }}>
                                            <FormGroup>
                                                <InputTextField
                                                    label="New: Receiver max per transaction"
                                                    value={this.state.receiverCurrencyLimit}
                                                    startAdornment={this.state.receiverCurrencySymbol}
                                                    inputType="number"
                                                    onChange={(value) => this.handleLimitChange('receiverCurrencyLimit', value)}
                                                    onBlur={() => this.handleLimitBlur('receiverCurrencyLimit')}
                                                    onFocus={() => this.handleLimitFocus('receiverCurrencyLimit')}
                                                />
                                            </FormGroup>
                                        </Box>
                                    </Box>
                                </fieldset>
                                <fieldset className="fieldset-border">
                                    <FormLabel>Regional Limits</FormLabel>
                                    <Box style={{ display: 'flex', gap: '20px' }}>
                                        <Box style={{ flex: 1 }}>
                                            <FormControl fullWidth>
                                                <FormLabel className="input-field-label">Country</FormLabel>
                                                <Select
                                                    color='secondary'
                                                    variant="outlined"
                                                    value={this.state.selectedCountry}
                                                    onChange={this.handleCountryChange}
                                                >
                                                    {this.state.countryList.map((country) => (
                                                        <MenuItem key={country.countryCode} value={country.countryCode}>
                                                            {country.countryName}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </Box>
                                        <Box style={{ flex: 2 }}>
                                            <FormControl fullWidth disabled={!this.state.selectedCountry}>
                                                <FormLabel className="input-field-label">Region</FormLabel>
                                                <Autocomplete
                                                    multiple
                                                    id="region-selector"
                                                    options={options}
                                                    disableCloseOnSelect
                                                    size="medium"
                                                    value={selectedValues}
                                                    onChange={this.handleRegionSelect}
                                                    renderTags={(tagValue, getTagProps) => {
                                                        const visibleTags = tagValue.slice(0, 2);
                                                        const remainingCount = tagValue.length - visibleTags.length;
                                                        return (
                                                            <>
                                                                {visibleTags.map((option, index) => (
                                                                    <Chip label={option.label} {...getTagProps({ index })} />
                                                                ))}
                                                                {remainingCount > 0 && <Chip label={`+${remainingCount} more`} />}
                                                            </>
                                                        );
                                                    }}
                                                    limitTags={2}
                                                    openOnFocus
                                                    selectOnFocus
                                                    getOptionLabel={(option) => option.label}
                                                    renderOption={(props, option) => {
                                                        const isSelected = this.state.selectedRegion.includes("All") || this.state.selectedRegion.includes(option.value);
                                                        return (
                                                            <li {...props}>
                                                                <Checkbox
                                                                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                                                                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                                                                    style={{ marginRight: 5 }}
                                                                    checked={isSelected}
                                                                    color="secondary"
                                                                />
                                                                {option.label}
                                                            </li>
                                                        );
                                                    }}
                                                    renderInput={(params) => <TextField {...params} placeholder="Search Regions" variant="outlined" />}
                                                />
                                            </FormControl>
                                        </Box>
                                        <Box style={{ flex: 1 }}>
                                            <FormGroup>
                                                <InputTextField
                                                    label="Maximum limit"
                                                    value={this.state.regionSpecificLimit}
                                                    startAdornment={this.state.senderCurrencySymbol}
                                                    inputType="number"
                                                    onChange={(value) => this.handleLimitChange('regionSpecificLimit', value)}
                                                    onBlur={() => this.handleLimitBlur('regionSpecificLimit')}
                                                    onFocus={() => this.handleLimitFocus('regionSpecificLimit')}
                                                    disabled={!this.state.selectedRegion.length}
                                                />
                                            </FormGroup>
                                        </Box>
                                    </Box>
                                    <Box style={{ marginTop: "20px" }}>
                                        <FormLabel>User tier limits</FormLabel>
                                        <Box style={{ display: 'flex', gap: '20px', alignItems: 'end' }}>
                                            <Box>
                                                <FormGroup>
                                                    <Box display="flex" alignItems="center" gap='10px' marginTop="15px" >
                                                        <GroupButton
                                                            options={limitOptions}
                                                            selectedValue={this.state.selectedPeriod}
                                                            onChange={this.handlePeriodChange}
                                                        />
                                                    </Box>
                                                </FormGroup>
                                            </Box>
                                            <Box>
                                                <span
                                                    style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer' }}
                                                    onClick={() => this.setState({ openViewDetails: true })}
                                                >
                                                    View Details
                                                </span>
                                                <CustomDialog
                                                    open={this.state.openViewDetails}
                                                    onClose={() => this.setState({ openViewDetails: false })}
                                                    title="Tier Thresholds"
                                                    tableData={this.getViewDetailsData()}
                                                />
                                            </Box>
                                        </Box>
                                        {this.state.selectedPeriod === 'daily' && (
                                            <Box style={{ marginTop: "20px" }}>
                                                <CheckBox
                                                    label="Daily limits ON/OFF"
                                                    checked={this.state.isDailyLimitEnabled}
                                                    onChange={this.handleLimitOnOff}
                                                />
                                            </Box>
                                        )}
                                        {this.state.selectedPeriod === 'weekly' && (
                                            <Box style={{ marginTop: "20px" }}>
                                                <CheckBox
                                                    label="Weekly limits ON/OFF"
                                                    checked={this.state.isWeeklyLimitEnabled}
                                                    onChange={this.handleLimitOnOff}
                                                />
                                            </Box>
                                        )}
                                        {this.state.selectedPeriod === 'monthly' && (
                                            <Box style={{ marginTop: "20px" }}>
                                                <CheckBox
                                                    label="Monthly limits ON/OFF"
                                                    checked={this.state.isMonthlyLimitEnabled}
                                                    onChange={this.handleLimitOnOff}
                                                />
                                            </Box>
                                        )}
                                        {this.state.selectedPeriod === "yearly" && (
                                            <Box style={{ marginTop: "20px" }}>
                                                <CheckBox
                                                    label="Yearly limits ON/OFF"
                                                    checked={this.state.isYearlyLimitEnabled}
                                                    onChange={this.handleLimitOnOff}
                                                />
                                            </Box>
                                        )}
                                        <Box style={{ display: 'flex', gap: '20px' }}>
                                            {this.state.selectedPeriod === 'hourly' && (
                                                <>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Standard"
                                                                value={this.state.standardHourlyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('standardHourlyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('standardHourlyLimit')}
                                                                onFocus={() => this.handleLimitFocus('standardHourlyLimit')}
                                                                disabled={!this.state.isHourlyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Premium"
                                                                value={this.state.premiumHourlyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('premiumHourlyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('premiumHourlyLimit')}
                                                                onFocus={() => this.handleLimitFocus('premiumHourlyLimit')}
                                                                disabled={!this.state.isHourlyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Business"
                                                                value={this.state.businessHourlyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('businessHourlyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('businessHourlyLimit')}
                                                                onFocus={() => this.handleLimitFocus('businessHourlyLimit')}
                                                                disabled={!this.state.isHourlyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                </>
                                            )}
                                            {this.state.selectedPeriod === 'daily' && (
                                                <>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Standard"
                                                                value={this.state.standardDailyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('standardDailyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('standardDailyLimit')}
                                                                onFocus={() => this.handleLimitFocus('standardDailyLimit')}
                                                                disabled={!this.state.isDailyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Premium"
                                                                value={this.state.premiumDailyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('premiumDailyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('premiumDailyLimit')}
                                                                onFocus={() => this.handleLimitFocus('premiumDailyLimit')}
                                                                disabled={!this.state.isDailyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Business"
                                                                value={this.state.businessDailyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('businessDailyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('businessDailyLimit')}
                                                                onFocus={() => this.handleLimitFocus('businessDailyLimit')}
                                                                disabled={!this.state.isDailyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}></Box>
                                                </>
                                            )}
                                            {this.state.selectedPeriod === 'weekly' && (
                                                <>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Standard"
                                                                value={this.state.standardWeeklyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('standardWeeklyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('standardWeeklyLimit')}
                                                                onFocus={() => this.handleLimitFocus('standardWeeklyLimit')}
                                                                disabled={!this.state.isWeeklyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Premium"
                                                                value={this.state.premiumWeeklyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('premiumWeeklyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('premiumWeeklyLimit')}
                                                                onFocus={() => this.handleLimitFocus('premiumWeeklyLimit')}
                                                                disabled={!this.state.isWeeklyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Business"
                                                                value={this.state.businessWeeklyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('businessWeeklyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('businessWeeklyLimit')}
                                                                onFocus={() => this.handleLimitFocus('businessWeeklyLimit')}
                                                                disabled={!this.state.isWeeklyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}></Box>
                                                </>
                                            )}
                                            {this.state.selectedPeriod === 'monthly' && (
                                                <>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Standard"
                                                                value={this.state.standardMonthlyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('standardMonthlyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('standardMonthlyLimit')}
                                                                onFocus={() => this.handleLimitFocus('standardMonthlyLimit')}
                                                                disabled={!this.state.isMonthlyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Premium"
                                                                value={this.state.premiumMonthlyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('premiumMonthlyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('premiumMonthlyLimit')}
                                                                onFocus={() => this.handleLimitFocus('premiumMonthlyLimit')}
                                                                disabled={!this.state.isMonthlyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Business"
                                                                value={this.state.businessMonthlyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('businessMonthlyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('businessMonthlyLimit')}
                                                                onFocus={() => this.handleLimitFocus('businessMonthlyLimit')}
                                                                disabled={!this.state.isMonthlyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}></Box>
                                                </>
                                            )}
                                            {this.state.selectedPeriod === 'yearly' && (
                                                <>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Standard"
                                                                value={this.state.standardYearlyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('standardYearlyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('standardYearlyLimit')}
                                                                onFocus={() => this.handleLimitFocus('standardYearlyLimit')}
                                                                disabled={!this.state.isYearlyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Premium"
                                                                value={this.state.premiumYearlyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('premiumYearlyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('premiumYearlyLimit')}
                                                                onFocus={() => this.handleLimitFocus('premiumYearlyLimit')}
                                                                disabled={!this.state.isYearlyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}>
                                                        <FormGroup>
                                                            <InputTextField
                                                                label="Business"
                                                                value={this.state.businessYearlyLimit}
                                                                startAdornment={this.state.senderCurrencySymbol}
                                                                inputType="number"
                                                                onChange={(value) => this.handleLimitChange('businessYearlyLimit', value)}
                                                                onBlur={() => this.handleLimitBlur('businessYearlyLimit')}
                                                                onFocus={() => this.handleLimitFocus('businessYearlyLimit')}
                                                                disabled={!this.state.isYearlyLimitEnabled}
                                                            />
                                                        </FormGroup>
                                                    </Box>
                                                    <Box style={{ flex: 1 }}></Box>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                </fieldset>
                                <fieldset className="fieldset-border">
                                    <FormLabel>Alerts</FormLabel>
                                    <Box style={{ display: 'flex', gap: '20px' }}>
                                        <Box style={{ flex: 1 }}>
                                            <FormGroup>
                                                <InputTextField
                                                    label="Threshold limit alert"
                                                    endAdornment="%"
                                                    value={this.state.thresholdLimit}
                                                    inputType="number"
                                                    onChange={(value) => {
                                                        let newValue = value;
                                                        if (parseFloat(newValue) < 0) {
                                                            newValue = '0';
                                                        } else if (parseFloat(value) > 100) {
                                                            newValue = '100';
                                                        }
                                                        this.setState({ thresholdLimit: newValue });
                                                    }}
                                                />
                                            </FormGroup>
                                        </Box>
                                        <Box style={{ flex: 1 }}></Box>
                                        <Box style={{ flex: 1 }}></Box>
                                        <Box style={{ flex: 1 }}></Box>
                                    </Box>
                                </fieldset>
                            </Box>
                        </FormGroup>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            this.handleClose('cancel');
                        }}>Cancel</Button>
                        <Button
                            disabled={!profileSettings.isGeneralSettingSaveBtnVisible}
                            onClick={() => {
                                this.setState({
                                    isSaveConfirmed: true
                                })
                            }} color='secondary' variant="contained">Save</Button>
                    </DialogActions>
                </Dialog>
                <AlertDialogPopop
                    open={this.state.isSaveConfirmed}
                    alertTitle='Confirmation'
                    alertBody={`Are you sure you want to save the settings?`}
                    onClose={(state_, action_) => {
                        this.setState({
                            isSaveConfirmed: false
                        }, () => {
                            if (action_ == "ok") {
                                this.handleClose('ok');
                            }
                        })
                    }}
                />
            </>
        )
    }

    // Method to handle close on save/cancel
    private handleClose = async (action: string) => {
        let selectedRegion = this.state.selectedRegion;

        const convertToNumber = (value: string) => parseFloat(value.replace(/,/g, ''));

        if (action === "ok") {
            const senderLimit = convertToNumber(this.state.senderCurrencyLimit);
            const receiverLimit = convertToNumber(this.state.receiverCurrencyLimit);
            const postData: any = {
                currency: "USD", // Added only for API needs
                threshold: convertToNumber(this.state.thresholdLimit),
                limitsByCategories: {
                    standard: {
                        //hourlyLimit: convertToNumber(this.state.standardHourlyLimit),
                        dailyLimit: convertToNumber(this.state.standardDailyLimit),
                        weeklyLimit: convertToNumber(this.state.standardWeeklyLimit),
                        monthlyLimit: convertToNumber(this.state.standardMonthlyLimit),
                        yearlyLimit: convertToNumber(this.state.standardYearlyLimit),
                        //isHourlyLimitEnabled: this.state.isHourlyLimitEnabled,
                        isDailyLimitEnabled: this.state.isDailyLimitEnabled,
                        isWeeklyLimitEnabled: this.state.isWeeklyLimitEnabled,
                        isMonthlyLimitEnabled: this.state.isMonthlyLimitEnabled,
                        isYearlyLimitEnabled: this.state.isYearlyLimitEnabled
                    },
                    premium: {
                        //hourlyLimit: convertToNumber(this.state.premiumHourlyLimit),
                        dailyLimit: convertToNumber(this.state.premiumDailyLimit),
                        weeklyLimit: convertToNumber(this.state.premiumWeeklyLimit),
                        monthlyLimit: convertToNumber(this.state.premiumMonthlyLimit),
                        yearlyLimit: convertToNumber(this.state.premiumYearlyLimit),
                        //isHourlyLimitEnabled: this.state.isHourlyLimitEnabled,
                        isDailyLimitEnabled: this.state.isDailyLimitEnabled,
                        isWeeklyLimitEnabled: this.state.isWeeklyLimitEnabled,
                        isMonthlyLimitEnabled: this.state.isMonthlyLimitEnabled,
                        isYearlyLimitEnabled: this.state.isYearlyLimitEnabled
                    },
                    business: {
                        //hourlyLimit: convertToNumber(this.state.businessHourlyLimit),
                        dailyLimit: convertToNumber(this.state.businessDailyLimit),
                        weeklyLimit: convertToNumber(this.state.businessWeeklyLimit),
                        monthlyLimit: convertToNumber(this.state.businessMonthlyLimit),
                        yearlyLimit: convertToNumber(this.state.businessYearlyLimit),
                        //isHourlyLimitEnabled: this.state.isHourlyLimitEnabled,
                        isDailyLimitEnabled: this.state.isDailyLimitEnabled,
                        isWeeklyLimitEnabled: this.state.isWeeklyLimitEnabled,
                        isMonthlyLimitEnabled: this.state.isMonthlyLimitEnabled,
                        isYearlyLimitEnabled: this.state.isYearlyLimitEnabled
                    }
                },
                stateLimit: {
                    countryCode: this.state.selectedCountry,
                    stateCode: selectedRegion,
                    limit: convertToNumber(this.state.regionSpecificLimit),
                },
                senderCurrency: {
                    countryId: this.state.senderCountryId,
                    limitAmount: senderLimit,
                },
                receiverCurrency: {
                    countryId: this.state.receiverCountryId,
                    limitAmount: receiverLimit,
                    paymentMethodId: this.state.receiverPaymentId,
                }
            };

            try {
                const rsp = await LimitService.updateGlobalLimitsV2(this.props.accessToken, postData);

                if (rsp.ok) {
                    if (this.props.onClose) {
                        //this.props.onClose(false, action);
                        await this.getGeneralSetting();
                        ShowAlert("Success", "Changes saved successfully!", "success");
                    }
                } else {
                    console.warn("Failed to update limits. Please try again.");
                }
            } catch (error) {
                console.error("Error updating global limits:", error);
            }
        } else if (this.props.onClose) {
            this.props.onClose(false, action);
        }
    };

    // Method to handle limit change
    handleLimitChange = (field: keyof GeneralSettingDialogPopopState, value: string) => {
        const sanitizedValue = sanitizeNumberInput(value);
        this.setState((prevState) => ({
            ...prevState,
            [field]: sanitizedValue,
        }));
    };

    // Method to handle limit blur
    handleLimitBlur = (field: keyof GeneralSettingDialogPopopState) => {
        const value = this.state[field];
        const n_ = typeof value === 'string' && value ? value : "0";

        const updatedAmount = amountFormat(
            parseFloat(n_.replace(/,/g, "")),
            "en-US",
            "USD"
        ).replace("$", "");

        this.setState((prevState) => ({
            ...prevState,
            [field]: updatedAmount,
            [`${field}InputType`]: "text",
        }));
    };

    // Method to handle limit focus for number input
    handleLimitFocus = (field: keyof GeneralSettingDialogPopopState) => {
        const value = this.state[field];
        if (typeof value === 'string') {
            const n_ = value.replace(/,/g, "");
            this.setState((prevState) => ({
                ...prevState,
                [field]: n_,
                [`${field}InputType`]: "number"
            }));
        }
    };

    // Handle sender currency change event
    handleSenderCurrencyChange = (event: SelectChangeEvent<string>) => {
        const selectedCountryId = event.target.value;
        const selectedCurrencyOption = this.state.senderCurrencyList.find(option => option.countryId === selectedCountryId);
        const selectedCurrency = selectedCurrencyOption ? selectedCurrencyOption.currencyCode : '';
        const selectedCurrencySymbol = selectedCurrencyOption ? selectedCurrencyOption.currencySymbol : '';
        const selectedCurrencyLimit = selectedCurrencyOption ? selectedCurrencyOption.limitAmount : "0.00";

        this.setState({
            senderCurrency: selectedCurrency,
            senderCurrencySymbol: selectedCurrencySymbol,
            //senderCurrencyLimit: selectedCurrencyLimit
        },
            () => {
                this.getGeneralSetting();
            }
        );
    };

    // Handle receiver currency change event
    handleReceiverCurrencyChange = (event: SelectChangeEvent<string>) => {
        const selectedPaymentMethodId = event.target.value;
        const selectedCurrencyOption = this.state.receiverCurrencyList.find(option => option.paymentMethodId === selectedPaymentMethodId);
        const selectedCountryId = selectedCurrencyOption ? selectedCurrencyOption.countryId : '';
        const selectedCurrency = selectedCurrencyOption ? selectedCurrencyOption.currencyCode : '';
        const selectedCurrencySymbol = selectedCurrencyOption ? selectedCurrencyOption.currencySymbol : '';
        const selectedCurrencyLimit = selectedCurrencyOption ? selectedCurrencyOption.limitAmount : "0.00";

        this.setState({
            receiverCurrency: selectedCurrency,
            receiverCurrencySymbol: selectedCurrencySymbol,
            receiverCountryId: selectedCountryId,
            receiverPaymentId: selectedPaymentMethodId,
            receiverCurrencyLimit: selectedCurrencyLimit
        })
    };

    // Handle country change event
    handleCountryChange = (event: SelectChangeEvent<string>) => {
        const selectedCountry = event.target.value;
        this.setState({ selectedCountry, selectedRegion: [] });
    };

    // Handle period change event
    handlePeriodChange = (newPeriod: string): void => {
        this.setState({ selectedPeriod: newPeriod });
    };

    // hHandle region select event
    handleRegionSelect = (event: any, values: Option[]) => {
        let selectedItems: string[] = values.map((item) => item.value);

        if (selectedItems.includes("All") && !this.state.selectedRegion.includes("All")) {
            selectedItems = ["All"]; // Set to ["All"]
        } else if (!selectedItems.includes("All") && this.state.selectedRegion.includes("All")) {
            selectedItems = []; // Deselect all
        } else if (selectedItems.includes("All") && selectedItems.length !== this.mapRegionsToOptions().length + 1) {
            selectedItems = selectedItems.filter((item) => item !== "All");
        } else if (!selectedItems.includes("All") && selectedItems.length === this.mapRegionsToOptions().length) {
            selectedItems = ["All"];
        }

        this.setState({ selectedRegion: selectedItems });
    };

    //Handle limit on/off event checkbox
    handleLimitOnOff = (checked: boolean) => {
        const { selectedPeriod,
            initialStandardHourlyLimit,
            initialStandardDailyLimit,
            initialStandardWeeklyLimit,
            initialStandardMonthlyLimit,
            initialStandardYearlyLimit,
            initialPremiumHourlyLimit,
            initialPremiumDailyLimit,
            initialPremiumWeeklyLimit,
            initialPremiumMonthlyLimit,
            initialPremiumYearlyLimit,
            initialBusinessHourlyLimit,
            initialBusinessDailyLimit,
            initialBusinessWeeklyLimit,
            initialBusinessMonthlyLimit,
            initialBusinessYearlyLimit,
        } = this.state;

        this.setState((prevState) => {
            let updatedState: any = {
                [`is${selectedPeriod.charAt(0).toUpperCase() + selectedPeriod.slice(1)}LimitEnabled`]: checked
            };
            // Reset limit values to 0.00 if the limit is disabled
            if (!checked) {
                switch (selectedPeriod) {
                    case 'hourly':
                        updatedState = {
                            ...updatedState,
                            standardHourlyLimit: '0.00',
                            premiumHourlyLimit: '0.00',
                            businessHourlyLimit: '0.00',
                        };
                        break;
                    case 'daily':
                        updatedState = {
                            ...updatedState,
                            standardDailyLimit: '0.00',
                            premiumDailyLimit: '0.00',
                            businessDailyLimit: '0.00',
                        };
                        break;
                    case 'weekly':
                        updatedState = {
                            ...updatedState,
                            standardWeeklyLimit: '0.00',
                            premiumWeeklyLimit: '0.00',
                            businessWeeklyLimit: '0.00',
                        };
                        break;
                    case 'monthly':
                        updatedState = {
                            ...updatedState,
                            standardMonthlyLimit: '0.00',
                            premiumMonthlyLimit: '0.00',
                            businessMonthlyLimit: '0.00',
                        };
                        break;
                    case 'yearly':
                        updatedState = {
                            ...updatedState,
                            standardYearlyLimit: '0.00',
                            premiumYearlyLimit: '0.00',
                            businessYearlyLimit: '0.00',
                        };
                        break;
                    default:
                        break;
                }
            } else { // Reset limit values to initial values if the limit is enabled
                switch (selectedPeriod) {
                    case 'hourly':
                        updatedState = {
                            ...updatedState,
                            standardHourlyLimit: initialStandardHourlyLimit,
                            premiumHourlyLimit: initialPremiumHourlyLimit,
                            businessHourlyLimit: initialBusinessHourlyLimit,
                        };
                        break;
                    case 'daily':
                        updatedState = {
                            ...updatedState,
                            standardDailyLimit: initialStandardDailyLimit,
                            premiumDailyLimit: initialPremiumDailyLimit,
                            businessDailyLimit: initialBusinessDailyLimit,
                        };
                        break;
                    case 'weekly':
                        updatedState = {
                            ...updatedState,
                            standardWeeklyLimit: initialStandardWeeklyLimit,
                            premiumWeeklyLimit: initialPremiumWeeklyLimit,
                            businessWeeklyLimit: initialBusinessWeeklyLimit,
                        };
                        break;
                    case 'monthly':
                        updatedState = {
                            ...updatedState,
                            standardMonthlyLimit: initialStandardMonthlyLimit,
                            premiumMonthlyLimit: initialPremiumMonthlyLimit,
                            businessMonthlyLimit: initialBusinessMonthlyLimit,
                        };
                        break;
                    case 'yearly':
                        updatedState = {
                            ...updatedState,
                            standardYearlyLimit: initialStandardYearlyLimit,
                            premiumYearlyLimit: initialPremiumYearlyLimit,
                            businessYearlyLimit: initialBusinessYearlyLimit,
                        };
                        break;
                    default:
                        break;
                }
            }

            return updatedState;
        });
    };

    // Method to update currency limit by currency code
    updateCurrencyLimitByCurrencyCode = () => {
        const { senderCurrency, senderCurrencyList, receiverCurrency, receiverCurrencyList } = this.state;

        const selectedReceiverCurrency = receiverCurrency ? receiverCurrencyList.find(
            (item) => item.currencyCode === receiverCurrency
        ) : receiverCurrencyList[0];

        const selectedSenderCurrency = senderCurrencyList.find(
            (item) => item.currencyCode === senderCurrency
        );

        if (selectedReceiverCurrency || selectedSenderCurrency) {
            this.setState(
                {
                    receiverCurrencySymbol: selectedReceiverCurrency?.currencySymbol || "0.00",
                    receiverCurrencyLimit: selectedReceiverCurrency?.limitAmount || "0.00",
                    receiverPaymentId: selectedReceiverCurrency?.paymentMethodId,
                    receiverCountryId: selectedReceiverCurrency?.countryId || "GH",
                    senderCurrencySymbol: selectedSenderCurrency?.currencySymbol || "0.00",
                    senderCurrencyLimit: selectedSenderCurrency?.limitAmount || "0.00",
                }
            );
        }
    };
}

export default GeneralSettingDialogPopop;